import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  Inline,
  TooltipRenderer,
  Box,
  IconWorkExperience,
  IconEducation,
  IconDocument,
  Columns,
  Column,
  Bleed,
  Hidden,
} from 'braid-design-system';

import { parseBulletPoints } from '../../../utils/parseBulletPoints';
import {
  LabelValueFields,
  type LabelValueField,
} from '../LabelValueFields/LabelValueFields';

import translations from './.vocab';

import * as styles from './FieldList.css';

export interface FieldSet {
  heading: string;
  subHeading?: string;
  subHeadingSecondary?: string;
  isVerified?: boolean;
  description?: string;
  descriptionSize?: 'smallMobile' | 'standard';
  labelValueFields?: LabelValueField[];
}

type FieldListIcon = 'workExperience' | 'education' | 'document';
type FieldListTone = 'brandAccent' | 'positive' | 'info' | 'neutral';

interface Props {
  fields: FieldSet[];
  icon?: FieldListIcon;
  iconTone?: FieldListTone;
  descriptionSize?: any;
}

export const FieldList = ({
  fields,
  icon,
  iconTone = 'neutral',
  descriptionSize = 'standard',
}: Props) => {
  const { t } = useTranslations(translations);

  const iconMap = {
    workExperience: IconWorkExperience,
    education: IconEducation,
    document: IconDocument,
  };

  const backgroundMap: Record<FieldListTone, string> = {
    brandAccent: 'brandAccentSoft',
    positive: 'positiveLight',
    info: 'infoLight',
    neutral: 'neutralSoft',
  };

  const Icon = icon ? iconMap[icon] : null;
  const background = backgroundMap[iconTone];

  return (
    <>
      {fields.map(
        (
          {
            heading,
            subHeading,
            subHeadingSecondary,
            isVerified,
            description,
            labelValueFields,
          },
          index,
        ) => (
          <Columns space="xsmall" key={index}>
            {Icon ? (
              <Column width="content">
                <Bleed top="xxsmall">
                  <Box
                    maxWidth="content"
                    background={background as any}
                    padding="xxsmall"
                    borderRadius="standard"
                  >
                    <Box style={{ opacity: '0.7' }}>
                      <Hidden below="tablet">
                        <Icon size="small" tone={iconTone as any} />
                      </Hidden>
                      <Hidden above="mobile">
                        <Icon size="xsmall" tone={iconTone as any} />
                      </Hidden>
                    </Box>
                  </Box>
                </Bleed>
              </Column>
            ) : null}

            <Column>
              <Stack space="medium">
                <Stack space="small">
                  <Inline alignY="center" space="xxsmall">
                    <Text weight="strong">{heading}</Text>
                    {isVerified ? (
                      <TooltipRenderer
                        id="verified-tick-tooltip"
                        tooltip={
                          <Text weight="regular">
                            {t('VERIFIED_BY_SEEK_PASS')}
                          </Text>
                        }
                      >
                        {({ triggerProps }) => (
                          <Box aria-label="verified" {...triggerProps}>
                            <Text size="xsmall" data={{ testid: 'isVerified' }}>
                              <SeekPassIconVerified />
                            </Text>
                          </Box>
                        )}
                      </TooltipRenderer>
                    ) : null}
                  </Inline>

                  {subHeading ? (
                    <Text data={{ testid: 'subHeading' }}>{subHeading}</Text>
                  ) : null}

                  {subHeadingSecondary ? (
                    <Text data={{ testid: 'subHeadingSecondary' }}>
                      {subHeadingSecondary}
                    </Text>
                  ) : null}
                </Stack>

                {description ? (
                  <Text data={{ testid: 'description' }} tone="secondary">
                    <span
                      className={
                        descriptionSize === 'smallMobile'
                          ? styles.smallMobileText
                          : ''
                      }
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {parseBulletPoints(description)}
                    </span>
                  </Text>
                ) : null}

                {labelValueFields && labelValueFields.length > 0 && (
                  <LabelValueFields fields={labelValueFields} />
                )}
              </Stack>
            </Column>
          </Columns>
        ),
      )}
    </>
  );
};
